import { config } from '../config';
import { requestToken, endSession } from '../auth/session';

type GraphQLResponse<T> = { data?: T; errors?: Array<{ message: string }> };

export async function graphql<T>(input: {
  query: string;
  variables?: Record<string, unknown>;
  token?: string;
  organizationId?: string;
}): Promise<T> {
  async function send(forceRenew = false): Promise<{ response: Response; body: GraphQLResponse<T> }> {
  const token = input.token ? await requestToken(input.token, forceRenew) : undefined;
  const response = await fetch(config.graphqlUrl, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(input.organizationId ? { 'x-organization-id': input.organizationId } : {})
    },
    body: JSON.stringify({ query: input.query, variables: input.variables ?? {} })
  });
  const body = await response.json() as GraphQLResponse<T>;
  return { response, body };
  }
  let { response, body } = await send();
  // Retry only authentication failures before resolver execution, never an
  // ambiguous network failure or a mutation's business-rule error.
  if (input.token && (response.status === 401 || body.errors?.some(error => /Context creation failed:.*"exp" claim timestamp check failed/.test(error.message)))) {
    ({ response, body } = await send(true));
    if (response.status === 401 || body.errors?.some(error => /Context creation failed:.*"exp" claim timestamp check failed/.test(error.message))) {
      await endSession();
      throw new Error('Your session has ended. Please sign in again.');
    }
  }
  if (!response.ok || body.errors?.length) throw new Error(body.errors?.[0]?.message ?? `Request failed (${response.status})`);
  if (!body.data) throw new Error('GraphQL response contained no data');
  return body.data;
}

export function activeOrganizationId(): string {
  return localStorage.getItem('payright-organization-id') || config.defaultOrganizationId;
}

export function initializeActiveOrganizationId(organizationIds: string[]): boolean {
  const current = activeOrganizationId();
  if (!organizationIds[0]) {
    if (current) localStorage.removeItem('payright-organization-id');
    return false;
  }
  if (organizationIds.includes(current)) return false;
  localStorage.setItem('payright-organization-id', organizationIds[0]);
  return true;
}
